import { memo, useCallback } from "react";
import type { ComponentProps } from "react";

import { IconButton, Row } from "@narmi/design_system";

import styles from "../../SearchBar.module.scss";

import type { RowProps } from "./types";

/** Narmi icon name, derived from NDS so it tracks the design system. */
type IconName = ComponentProps<typeof IconButton>["name"];

interface SingleRowProps extends RowProps {
  iconRight: IconName;
  onClickRight?: () => void;
  iconLeft?: string;
}

const SingleRow = ({
  onClickRow,
  route,
  title,
  iconRight,
  onClickRight,
  iconLeft,
}: SingleRowProps) => {
  const handleActivate = useCallback(() => {
    if (onClickRow) {
      onClickRow();
    } else if (route) {
      window.location.assign(route);
    }
  }, [onClickRow, route]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleActivate();
      }
    },
    [handleActivate]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      className={styles["results-row-container"]}
      data-testid="search-single-row"
    >
      <Row alignItems="center" gapSize="xs">
        {iconLeft && (
          <Row.Item
            shrink
            className={`narmi-icon-${iconLeft}`}
            aria-hidden="true"
          />
        )}
        <Row.Item className={styles["single-line-text"]}>{title}</Row.Item>
        <Row.Item shrink className={styles["row-icon-button"]}>
          <IconButton
            name={iconRight}
            textSize="l"
            onClick={(event: React.MouseEvent) => {
              if (onClickRight) {
                event.stopPropagation();
                onClickRight();
              } else {
                handleActivate();
              }
            }}
          />
        </Row.Item>
      </Row>
    </div>
  );
};

export default memo(SingleRow);
